<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useIntersectionObserver, useThrottleFn, useDebounceFn } from '@vueuse/core'

import { faSearch } from '@fortawesome/free-solid-svg-icons'

import InputWithSubmitIcon from '@/components/ui/input_with_submit_icon.vue'
import ServiceProviderPill from '@/components/service_provider/service_provider_pill.vue'
import ServiceProviderPillSkeleton from '@/components/service_provider/service_provider_pill_skeleton.vue'

import { useServiceProviderSearchStore } from '@/stores/service_provider_search.js'
import { useIntelichekFetch } from '@/lib/intelichek-fetch.js'
import { useGeocodeStore } from '@/stores/geocode.js'

// Stores
const serviceProviderSearchStore = useServiceProviderSearchStore()
const geocodeStore = useGeocodeStore()

// I18n
const { t, tm } = useI18n()



// ServiceProviderQuery
const scrollTarget = ref()
const targetIsVisible = ref(false)
const page = ref(1)
const serviceProvidersData = ref([])

const { stop } = useIntersectionObserver(
  scrollTarget,
  ([entry]) => {
    targetIsVisible.value = (entry?.isIntersecting && !isServiceProvidersLoading.value) || false
  }
)

const hasValidLocation = computed(() => {
  return serviceProviderSearchStore.filters.location && 
         serviceProviderSearchStore.filters.location.latitude && 
         serviceProviderSearchStore.filters.location.longitude
})

const serviceProviderUrl = computed(() => {
  if (!hasValidLocation.value) return null
  const url = serviceProviderSearchStore.buildQueryString(page.value)
  return url
})
const { isFetching: isServiceProvidersLoading, data: serviceProvidersDataRaw } = useIntelichekFetch(serviceProviderUrl, { refetch: true }).json()

const nextPage = useThrottleFn(() => {
  page.value = page.value + 1
}, 400)

watch(targetIsVisible, (value) => {
  if(value && page.value < 4) {
    nextPage()
  }
})

watch(serviceProvidersDataRaw, (data) => {
  serviceProvidersData.value.push(...(data?.data))
})

watch(() => serviceProviderSearchStore.filters.location, () => {
  serviceProvidersData.value = [];
  page.value = 1
})

watch(() => serviceProviderSearchStore.filters.query_string, () => {
  serviceProvidersData.value = [];
  page.value = 1
})

watch(() => serviceProviderSearchStore.filters.type, () => {
  serviceProvidersData.value = [];
  page.value = 1
}, { deep: true })

// Computed
const serviceProvidersByTier = computed(() => {
  if (!serviceProvidersData.value) return { gold: [], bronze: [], free: [] };
  return {
    gold: serviceProvidersData.value.filter(sp => sp.sponsored_scope_tier === 2),
    bronze: serviceProvidersData.value.filter(sp => sp.sponsored_scope_tier === 1),
    free: serviceProvidersData.value.filter(sp => !sp.sponsored_scope_tier)
  };
})

// Variables
const searchQuery = ref(serviceProviderSearchStore.filters.query_string)

const debouncedSearch = useDebounceFn((value) => {
  serviceProviderSearchStore.filters.query_string = value
}, 500)

watch(searchQuery, (newValue) => {
  debouncedSearch(newValue)
})

// Auto-refresh logic
let autoRefreshTimer = null

const startAutoRefresh = () => {
  if (autoRefreshTimer) clearTimeout(autoRefreshTimer)
  autoRefreshTimer = setTimeout(() => {
    if (!hasValidLocation.value && !geocodeStore.isLoading && !geocodeStore.isError) {
      geocodeStore.$reset()
      page.value = 1
      serviceProvidersData.value = []
    }
  }, 5000)
}

const clearAutoRefresh = () => {
  if (autoRefreshTimer) {
    clearTimeout(autoRefreshTimer)
    autoRefreshTimer = null
  }
}

watch([hasValidLocation, () => serviceProvidersData.value.length], ([hasLocation, dataLength]) => {
  if (!hasLocation || dataLength === 0) {
    startAutoRefresh()
  } else {
    clearAutoRefresh()
  }
})

onMounted(() => {
  // Add a small delay to ensure all stores are initialized
  setTimeout(() => {
    if (!hasValidLocation.value || serviceProvidersData.value.length === 0) {
      startAutoRefresh()
    }
  }, 100)
})

onUnmounted(() => {
  clearAutoRefresh()
})

</script>

<template>
  <div>
    <div v-if="hasValidLocation" class="max-w-96">
      <div v-if="isServiceProvidersLoading && serviceProvidersData.length === 0" class="animate-pulse">
        <div class="h-10 bg-gray-300 rounded w-full"></div>
      </div>
      <InputWithSubmitIcon v-else :placeholder="t('service_providers.search_results.search_service_providers')"
                           :icon="faSearch" v-model="searchQuery"
                           :showSubmit="false"></InputWithSubmitIcon>
    </div>

    <div v-if="!hasValidLocation" class="mt-6 text-center py-12">
      <div class="bg-blue-50 rounded-lg p-8 max-w-md mx-auto">
        <div class="text-blue-600 mb-4">
          <svg class="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Select Your Location</h3>
        <p class="text-gray-600">We're ready to show you amazing service providers! Please select your location from the filters above to get started.</p>
      </div>
    </div>

    <div v-else>
      <h2 class="mt-6 text-xl">
        {{ t('service_providers.search_results.closest_to_location', { location: serviceProviderSearchStore.filters.location?.nickname }) }}
      </h2>

      <div v-if="isServiceProvidersLoading && serviceProvidersData.length === 0">
        <div class="my-2 flex flex-col gap-2">
          <ServiceProviderPillSkeleton v-for="n in 2" :key="n" />
        </div>
        <div class="my-2 flex flex-col gap-2">
          <ServiceProviderPillSkeleton v-for="n in 2" :key="n" />
        </div>
        <hr />
        <div class="my-2 grid xl:grid-cols-2 gap-2">
          <ServiceProviderPillSkeleton v-for="n in 4" :key="n" />
        </div>
      </div>

      <div v-else>
        <div class="my-2 flex flex-col gap-2">
          <ServiceProviderPill v-for="serviceProvider in serviceProvidersByTier.gold"
                               :service-provider="serviceProvider"></ServiceProviderPill>
        </div>

        <div class="my-2 flex flex-col gap-2">
          <ServiceProviderPill v-for="serviceProvider in serviceProvidersByTier.bronze"
                               :service-provider="serviceProvider"></ServiceProviderPill>
        </div>

        <hr />

        <div class="my-2 grid xl:grid-cols-2 gap-2">
          <ServiceProviderPill v-for="serviceProvider in serviceProvidersByTier.free"
                               :service-provider="serviceProvider"></ServiceProviderPill>
        </div>
      </div>

      <div v-if="isServiceProvidersLoading" class="py-8 flex items-center justify-center">
        <div
          class="inline-block h-16 w-16 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
          role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>

      <div ref="scrollTarget"></div>

      <div class="flex justify-center">
        <button @click="nextPage()" class="rounded-full bg-[#b05b34] text-white py-2 px-6 hover:opacity-90" :disabled="isServiceProvidersLoading">Load More</button>
      </div>
    </div>

    <div class="mt-4">
      <h4>{{ t('service_providers.service_provider_request.title') }}</h4>
      <p v-for="blurb in tm('service_providers.service_provider_request.body')" class="text-xs mt-1 text-gray-800" v-html="blurb"></p>
    </div>
    <div class="mt-4">
      <p v-for="blurb in tm('service_providers.service_provider_disclaimer')" class="text-xs mt-1 text-gray-800" v-html="blurb"></p>
    </div>

  </div>
</template>


<style lang="scss" scoped>
@import '@/assets/styles/views/service_providers/search_results';
</style>
